import Vue from "vue";
import axios, { AxiosInstance } from "axios";
import PromiseControlHandler from "@/helpers/PromiseControlhandler";
import { sortLoads } from "@/helpers/loadService";

// States
export const state = Vue.observable({
  axiosSgpInstance: {} as AxiosInstance,
  axiosApiRouteInstance: {} as AxiosInstance,
  progressLinear: false as boolean,
  urlSgp: "" as string,
  token: "" as string,
  cdCliente: 0 as number,
  grupo: 0 as number,
  locale: "" as string,
  unidade: [] as Array<number>,
  loads: [] as Array<Load.Load>,
  warehouses: [] as Array<TenderMixOperator.Warehouse>,
  warehouseSelected: {} as TenderMixOperator.Warehouse,
  equipGroups: [] as Array<TenderMixOperator.EquipGroup>,
  equipGroupSelected: {} as TenderMixOperator.EquipGroup,
  connected: true as boolean,
  statusTenderSelected: {} as Load.StatusLoadSelected,
  environmentStage: "P" as "P" | "H" | "D",

  // tender dispath
  workplaces: [],
  equipments: [] as Array<Tender.Equipamento>,
  warehouseTDSelected: {} as TenderMixOperator.Warehouse,
  equipmentTDSelected: {} as Tender.Equipamento
});

// Getters
export const getters = {
  getAxiosSgpInstace: () => state.axiosSgpInstance,
  getAxiosApiRouteInstace: () => state.axiosApiRouteInstance,
  getProgressLinear: () => state.progressLinear,
  getToken: () => state.token,
  getUrlSgp: () => state.urlSgp,
  getCdCliente: () => state.cdCliente,
  getGrupo: () => state.grupo,
  getLocale: () => state.locale,
  getUnidade: () => state.unidade,
  getLoads: () => state.loads,
  getWarehouses: () => state.warehouses,
  getWarehouseSelected: () => state.warehouseSelected,
  getEquipGroups: () => state.equipGroups,
  getEquipGroupSelected: () => state.equipGroupSelected,
  getConnection: () => state.connected,
  getStatusTenderSelected: () => state.statusTenderSelected,
  getEnvironmentStage: () => state.environmentStage,

  // Single Tender
  getWorkplaces: () => state.workplaces,
  getEquipments: () => state.equipments.filter(e => e.isTender === 1),
  getTDWarehouseSelected: () => state.warehouseTDSelected,
  getTDEquipmentSelected: () => state.equipmentTDSelected
};

// Mutations
export const mutations = {
  setAxios: () => {
    state.axiosSgpInstance = axios.create({
      baseURL: state.urlSgp,
      headers: {
        "Content-type": "application/json",
        "x-auth-token": state.token
      },
      timeout: 100000
    });
    state.axiosApiRouteInstance = axios.create({
      // baseURL: process.env.VUE_APP_ROUTE_API,
      baseURL: "https://routes.saas-solinftec.com/",
      headers: {
        "Content-type": "application/json",
        "x-auth-token": state.token
      },
      timeout: 100000
    });
  },
  setProgressLinear: (value: boolean) => (state.progressLinear = value),
  setToken: (token: string) => (state.token = token),
  setUrlSgp: (urlSgp: string) => (state.urlSgp = urlSgp),
  setCdCliente: (cdCliente: number) => (state.cdCliente = cdCliente),
  setGrupo: (grupo: number) => (state.grupo = grupo),
  setLocale: (locale: string) => (state.locale = locale),
  setUnidade: (unidade: number[]) => (state.unidade = unidade),
  setLoads: (loads: Array<Load.Load>) => (state.loads = loads),
  setWarehouses: (warehouses: Array<TenderMixOperator.Warehouse>) =>
    (state.warehouses = warehouses),
  setWarehouseSelected: (warehouseSelected: TenderMixOperator.Warehouse) =>
    (state.warehouseSelected = warehouseSelected),
  setEquipGroups: (equipGroups: Array<TenderMixOperator.EquipGroup>) =>
    (state.equipGroups = equipGroups),
  setEquipGroupSelected: (equipGroupSelected: TenderMixOperator.EquipGroup) =>
    (state.equipGroupSelected = equipGroupSelected),
  setConnection: (connected: boolean) => (state.connected = connected),
  setStatusTenderSelected: (statusTenderSelected: Load.StatusLoadSelected) =>
    (state.statusTenderSelected = statusTenderSelected),
  setEnvironmentStage: (environmentStage: "P" | "H" | "D") => {
    state.environmentStage = environmentStage;
  },
  setWorkplaces: (workplaces: []) => (state.workplaces = workplaces),
  setEquipments: (equipments: Array<Tender.Equipamento>) => (state.equipments = equipments),
  setTDWarehouseSelected: (warehouseSelected: TenderMixOperator.Warehouse) =>
      (state.warehouseTDSelected = warehouseSelected),
  setTDEquipmentSelected: (equipmentSelected: Tender.Equipamento) =>
      (state.equipmentTDSelected = equipmentSelected)

};

// Actions
export const actions = {
  /* Ações para exibir a barra de progresso */
  showProgress: () => mutations.setProgressLinear(true),
  hideProgress: () => mutations.setProgressLinear(false),

  /* Ações para conexão de internet */
  onConnection: () => mutations.setConnection(true),
  offConnection: () => mutations.setConnection(false),

  /**
   * Ação para buscar os loads
   */
  fetchLoads: () => {
    const path = `/tender/upcoming-loads?clientId=${getters.getCdCliente()}`;
    actions.showProgress();
    return PromiseControlHandler(path)
      .then((result: { data: {} }) => result.data)
      .then((result: Array<Load.Load>) => {
        mutations.setLoads(sortLoads(result));
        actions.hideProgress();
      })
      .catch((err: string | undefined) => {
        actions.hideProgress();
        throw new Error(err);
      });
  },

  /**
   * Ação que gera trip para os loads a partir do tender
   */
  generateLoads: (tenderId: number): Promise<void> => {
    const path = `/tender/upcoming-loads/generate/tender/${tenderId}`;
    return PromiseControlHandler(path)
      .then((result: { data: {} }) => result.data)
      .then((result: Promise<void>) => result)
      .catch((err: string | undefined) => {
        throw new Error(err);
      });
  },

  changeAutoLoad: (tenderId: number, autoload: boolean): Promise<void> => {
    return state.axiosSgpInstance
      .put(`/tender/change-autoload?tenderId=${tenderId}&autoload=${autoload}`)
      .then(result => result.data)
      .then((result: Promise<void>) => result)
      .catch(err => {
        throw new Error(err);
      });
  },

  /**
   * Ação para buscar as warehouses (locais) para o filtro select
   */
  fetchWirehouse: () => {
    return state.axiosSgpInstance
      .get(`/locais?search=`)
      .then(result => result.data)
      .then((result: { data: Array<TenderMixOperator.Warehouse> }) => {
        mutations.setWarehouses(result.data);
      })
      .catch(err => {
        throw new Error(err);
      });
  },

  /**
   * Ação para buscar as warehouses (locais) para o filtro select
   */
  fetchEquipGroups: () => {
    return state.axiosSgpInstance
      .get(`/config-proc-ordem-servico/all`)
      .then(result => result.data)
      .then((result: { data: Array<TenderMixOperator.EquipGroup> }) => {
        mutations.setEquipGroups(result.data);
      })
      .catch(err => {
        throw new Error(err);
      });
  },

  mapGenerateCarRoute: (coordinates: any) => {
    return state.axiosApiRouteInstance
      .get(
        "route?no_transform=true" +
          "&weightting=shortest" +
          "&vehicle=sprayer" +
          "&geometries=geojson" +
          "&point=" +
          coordinates
      )
      .then(result => result.data)
      .then((result: Promise<Response.RouteCalculation>) => result)
      .catch(err => {
        throw new Error(err);
      });
  },

  dispatchTender: (tender: TenderDispath.TenderDispath) => {
    return state.axiosSgpInstance
      .post(`/tender/dispatch?ignoreValidation=true`, tender)
      .then(result => result.data)
      .then((result: Promise<void>) => result)
      .catch(err => {
        throw new Error(err);
      });
  },

  fetchEquipments: (unidades: number[]): Promise<boolean> => {
    return state.axiosSgpInstance
      .post("/equipamentos/monitoramento", {
        unidades
      })
      .then(result => result.data)
      .then((result: Tender.ResponseEquipamento) => {
        mutations.setEquipments(result.equipamentos);
        return true;
      })
      .catch(err => {
        console.error(err);
        return false;
      });
  },

  checkTenderIsBusy: (tenderId: number) => {
    return state.axiosSgpInstance
      .get(`/tender/isbusy?tenderId=${tenderId}`)
      .then(result => result.data)
      .then((result: Promise<string[]>) => result)
      .catch(err => {
        throw new Error(err);
      });
  },

  changeStatusDispatch: (tenderId: number, status: string) => {
    return state.axiosSgpInstance
      .put(`/tender/dispatch/${tenderId}/change-status?status=${status}`)
      .then(result => result.data)
      .then((result: Promise<void>) => result)
      .catch(err => {
        throw new Error(err);
      });
  },

  getDispatch: (tenderId: number) => {
    return state.axiosSgpInstance
      .get(`/tender/dispatch/truck/${tenderId}`)
      .then(result => result.data)
      .then((result: Promise<TenderDispath.TenderDispath>) => result)
      .catch(err => {
        throw new Error(err);
      });
  }
};
